/* eslint-disable react/jsx-props-no-spreading */
import * as promises from '../../../../shared/promises';
import * as _ from 'lodash';
import PropTypes from 'prop-types';
import { Component } from 'react';
import { Button, Modal } from 'react-bootstrap';
import DropZone from 'react-dropzone';
import { connect } from 'react-redux';
import type { ProgressEvent } from 'superagent';
import { FileType } from '../../../../shared/fileUpload';
import { getSignedUrl, triggerBuilds, uploadFile } from '../../../api/componentUploader';
import { window } from '../../../globals';
import { userHasPermissionToViewAnyProject } from '../../../helpers/permissions';
import TeamsModule from '../../../redux/teams';
import type { FileUploadMetadata, Team } from '../../../types';
import { archiveUploadAccept, nuc, sbomUploadAccept, validArchiveUploadExtensions, validSBOMUploadExtensions } from '../../../utils';
import Icon, { icons, IconText } from '../../generic/Icon';
import Subnav from '../../generic/Subnav';
import SelectTeam, { teamsUserCanAddProject } from '../SelectTeam';
import UploadComponentHeader from './UploadComponentHeader';
import UploadComponentTable from './UploadComponentTable';
import { errorMessageFromResponse } from '../../../helpers/errors';
import type { FailedFetchResponse } from '../../../helpers/errors';
import styles from './UploadComponentPage.module.less';

export type FileToUpload = File & {
  customName?: string;
  revision?: string;
  // needs to be cleaned up on unmount
  preview?: any;
};

export type FileFieldUpdateDelegate = (field: 'customName' | 'revision', value: string, file: FileToUpload) => void;

type ReduxProps = {
  allTeams: Team[];
  fetchAllTeams: (filters: object) => void;
};

type UploadComponentPageProps = ReduxProps & {
  fileType: FileType;
};

type FileStatus = {
  bytesUploaded: number;
  isSuccessfullyUploaded: boolean;
  errorMessage?: string;
};

export type FileToUploadAndStatus = { file: FileToUpload; status: FileStatus };

type UploadComponentPageState = {
  files: { [fileName: string]: FileToUploadAndStatus };
  assignToTeams: Team[];
  showAssignToTeamsModal: boolean;
  isUploading: boolean;
};

class UploadComponentsPage extends Component<UploadComponentPageProps, UploadComponentPageState> {
  // eslint-disable-next-line react/static-property-placement
  static contextTypes = {
    // eslint-disable-next-line react/forbid-prop-types
    router: PropTypes.object.isRequired,
  };

  constructor(props: UploadComponentPageProps) {
    super(props);

    this.state = {
      files: {},
      assignToTeams: [],
      showAssignToTeamsModal: false,
      isUploading: false,
    };
  }

  componentDidMount() {
    if (window.user) {
      this.props.fetchAllTeams({ organizationId: window.user.organizationId });
    }
  }

  componentWillUnmount() {
    // prevents memory leaks because DropZone does not manage dropped files
    this.cleanDroppedFiles();
  }

  get validFileExtensions() {
    return this.props.fileType === 'archive' ? validArchiveUploadExtensions : validSBOMUploadExtensions;
  }

  get acceptFileTypes() {
    return this.props.fileType === 'archive' ? archiveUploadAccept : sbomUploadAccept;
  }

  get fetcher() {
    return this.props.fileType === 'archive' ? 'archive' : 'sbom';
  }

  get backToServicesURL() {
    return this.props.fileType === 'archive' ? '/projects/import/cloud' : '/projects/import';
  }

  cleanDroppedFiles(): void {
    this.getFiles().forEach(file => window.URL.revokeObjectURL(file.preview));
  }

  getFiles(): FileToUpload[] {
    return Object.values(this.state.files).map(f => f.file);
  }

  // get list of files that have not been successfully uploaded
  getNonUploadedFiles(): FileToUpload[] {
    return Object.values(this.state.files).filter(f => !f.status.isSuccessfullyUploaded).map(f => f.file);
  }

  onProgressUpdate(file: FileToUpload, progress: ProgressEvent) {
    if (!this.state.files[file.name]) {
      return;
    }

    this.setState(state => {
      const nextFileToUpload = (state.files[file.name] as FileToUploadAndStatus);
      nextFileToUpload.status = {
        bytesUploaded: progress.loaded,
        errorMessage: undefined,
        isSuccessfullyUploaded: false,
      };
      return {
        ...state,
        files: {
          ...state.files,
          [file.name]: nextFileToUpload,
        },
      };
    });
  }

  applyCustomName = (file: FileToUpload) => {
    // eslint-disable-next-line no-param-reassign
    file.customName = this.parsePackageNameFromFileName(file.name);
    return file;
  };

  onDrop = async (acceptedFiles: File[]) => {
    if (!acceptedFiles.length) {
      return;
    }

    this.setState(prevState => {
      const nextFiles = _.cloneDeep(prevState.files);
      acceptedFiles.forEach((file: FileToUpload) => {
        nextFiles[file.name] = {
          file: this.applyCustomName(file),
          status: {
            bytesUploaded: 0,
            errorMessage: undefined,
            isSuccessfullyUploaded: false,
          },
        };
      });

      return {
        ...prevState,
        files: nextFiles,
      };
    });
  };

  parsePackageNameFromFileName(fileName: string): string {
    return this.validFileExtensions.reduce((simplifiedFileName, extension) =>
      simplifiedFileName.split(extension)[0] as string, fileName);
  }

  promptForTeamAssignment = () => {
    // If the user has no teams to assign, just continue without prompting
    if (teamsUserCanAddProject(this.props.allTeams).length === 0) {
      this.uploadFiles();
      return;
    }

    // Prompt for assignment to teams
    this.setState({ showAssignToTeamsModal: true, assignToTeams: [] });
  };

  teamAssignmentClosed = () => {
    this.setState({ showAssignToTeamsModal: false });
  };

  uploadFiles = async (): Promise<void> => {
    const files = this.getNonUploadedFiles();
    // Set revision to timestamp if not provided
    files.forEach(file => {
      if (_.isEmpty(file.customName)) {
        throw new Error('All selected files must have a name');
      }
    });

    this.setState({ isUploading: true });

    // If no revision provided, we default to the current timestamp. We don't set the field directly
    // on the file however to reduce confusion (i.e.: how did this field get set?)
    const defaultRevision = Date.now().toString();
    // handle errors on a file by file basis, so partial successes become possible
    await promises.map(files, async (file) => {
      const fileRevision = file.revision || defaultRevision;
      try {
        const uploadUrl = await getSignedUrl(file.customName as string, fileRevision, this.props.fileType);
        // upload file to S3/Minio
        await uploadFile(file, uploadUrl, (progress: ProgressEvent) => {
          this.onProgressUpdate(file, progress);
        });
        // Create Build in FOSSA backend
        await triggerBuilds(
          {
            archives: [
              {
                packageSpec: file.customName,
                revision: fileRevision,
                fileType: this.props.fileType,
              } as FileUploadMetadata,
            ],
            selectedTeams: this.state.assignToTeams.map((team) => _.pick(team, 'id', 'name', 'organizationId')),
          },
          { fileType: this.props.fileType },
        );
        // set file as "done" uploading
        this.setState(nextState => {
          const nextFile = nextState.files[file.name] as FileToUploadAndStatus;
          nextFile.status.isSuccessfullyUploaded = true;
          return {
            ...nextState,
            files: {
              ...nextState.files,
              [file.name]: nextFile,
            },
          };
        });
      } catch (error) {
        this.setState(nextState => {
          const nextFile = nextState.files[file.name] as FileToUploadAndStatus;
          nextFile.status.errorMessage = errorMessageFromResponse(error as FailedFetchResponse);
          return {
            ...nextState,
            files: {
              ...nextState.files,
              [file.name]: nextFile,
            },
          };
        });
      }
    }, { concurrency: 3 });

    // redirect to the project page if all the files were uploaded successfully
    if (this.getNonUploadedFiles().length === 0) {
      const { fileType } = this.props;
      //  If there was only a single project uploaded, we can redirect to that project instead of the projects dashboard.
      if (files.length === 1) {
        //  If they are uploading a single SBOM file, it's more helpful to the
        //  user to be redirected to the actual project page
        if (fileType === FileType.SBOM || fileType === FileType.Archive) {
          const projectName = files?.[0]?.customName;
          const revision = files?.[0]?.revision ?? defaultRevision;
          if (projectName && revision) {
            const fetcherPrefix = fileType === FileType.SBOM ? 'sbom' : 'archive';
            //  We can infer the project locator now, from the customName.
            const projectLocator = nuc(`${fetcherPrefix}+${window.user?.organizationId}/${projectName}`);
            //  Note that this assumes that the default branch is called "master".
            //  This is safe because when SBOMs are imported, we use `master` as the default branch anyway.
            return this.context.router.push(`/projects/${projectLocator}/refs/branch/master/${nuc(revision)}`);
          }
        }
      }

      return this.context.router.push('/projects');
    }

    this.setState({ isUploading: false });
  };

  onTeamsSelected = (teams: Team[]) => {
    this.setState({ assignToTeams: teams });
  };

  onTeamsSelectionImport = () => {
    this.teamAssignmentClosed();
    this.uploadFiles();
  };

  onRemoveFile = (fileName: string) => {
    if (!this.state.files[fileName]) {
      return;
    }

    this.setState(nextState => {
      const newFiles = _.cloneDeep(nextState.files);
      delete newFiles[fileName];
      return {
        ...nextState,
        files: newFiles,
      };
    });
  };

  onUpdateFileField = (field: 'customName' | 'revision', value: string, file: FileToUpload) => {
    if (!this.state.files[file.name]) {
      return;
    }

    this.setState((state: UploadComponentPageState) => {
      const nextFile = (state.files[file.name] as FileToUploadAndStatus).file;
      nextFile[field] = value;
      return {
        ...state,
        files: {
          ...state.files,
          [file.name]: {
            file: nextFile,
            status: { bytesUploaded: 0, errorMessage: undefined, isSuccessfullyUploaded: false },
          },
        },
      };
    });
  };

  render() {
    const allFiles = this.getFiles();
    const allNonUploadedFiles = this.getNonUploadedFiles();
    const filesHaveBeenSelected = !!allFiles.length;

    return (
      <div>
        {/* don't give an easy way to navigate away from the page when upload has started */}
        {!this.state.isUploading && (
          <Subnav>
            <Subnav.Item to={this.backToServicesURL}>
              <IconText
                icon={icons.arrowLeft}
                text="Back to Services"
                />
            </Subnav.Item>
          </Subnav>
        )}
        <div className="container">
          <h1 className={`${styles.heading} text-left margin-bottom-xl`}>
            {this.props.fileType === 'archive' ? 'Archive' : 'SBOM'} Upload
          </h1>
          <div className={`${styles.container} card`}>
            <div className={styles.header}>
              <UploadComponentHeader
                numUploads={allNonUploadedFiles.length}
                onDropAdditionalFiles={this.onDrop}
                acceptedFileTypes={this.acceptFileTypes}
                onUpload={this.promptForTeamAssignment}
                isUploadEnabled={allNonUploadedFiles.length > 0 && !this.state.isUploading}
              />
            </div>
            {filesHaveBeenSelected &&
              <UploadComponentTable
                files={Object.values(this.state.files)}
                onFileFieldUpdated={this.onUpdateFileField}
                onRemoveFile={this.onRemoveFile}
                fetcher={this.fetcher}
              />}
            <div className={styles.addFile}>
              <h4 className="margin-bottom-md margin-top-md">Add File(s):</h4>
              <DropZone onDrop={this.onDrop} accept={this.acceptFileTypes}>
                {
                  ({ getRootProps, getInputProps }) => (
                    <div
                      className="clickable"
                      {...getRootProps()}
                      style={{
                        width: '100%',
                        border: 'dashed 2px #ccc',
                        padding: '50px',
                        borderRadius: '3px',
                        textAlign: 'center',
                      }}>
                      <span style={{ fontSize: '60px', color: '#aaa' }}>
                        <Icon icon={icons.fileO} />
                      </span><br /><br /><br /><p style={{ fontSize: '20px' }}>Drop files or click here to upload</p><i>{this.validFileExtensions.join(', ')}</i>
                      <input {...getInputProps()}/>
                    </div>
                  )
                }
              </DropZone>
            </div>
          </div>
        </div>
        <Modal show={this.state.showAssignToTeamsModal} onHide={this.teamAssignmentClosed}>
          <Modal.Header>
            <Modal.Title>
              Assign Project to Teams
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <SelectTeam
              allTeams={this.props.allTeams}
              selectedTeams={this.state.assignToTeams}
              onTeamsSelected={this.onTeamsSelected}
              permissionKind="project"
            />
          </Modal.Body>
          <Modal.Footer>
            <Button onClick={this.teamAssignmentClosed}>Cancel</Button>
            <Button
              onClick={this.onTeamsSelectionImport}
              bsStyle="success"
              disabled={!userHasPermissionToViewAnyProject() && this.state.assignToTeams.length === 0}
            >
              Import Projects
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    );
  }
}

const mapStateToProps = (state: UploadComponentPageState) => ({
  allTeams: TeamsModule.selectors.getTeamList(state),
});

const mapDispatchToProps = {
  fetchAllTeams: TeamsModule.actions.fetchAll,
};

export default connect(mapStateToProps, mapDispatchToProps)(UploadComponentsPage);
